<template>
    <div class="muven-card">
        <div class="muven-card-title">
            <p>
                Filas de integração

                <fa icon="sync-alt" spin class="float-right channel-queue-health-loading" v-if="loading" />
            </p>

            <p class="muven-card-subtitle">
                Itens aguardando processamento nas filas de cada canal ativo
            </p>
        </div>

        <div v-show="pendingChannels.length">
            <highcharts :options="chartOptions" />
        </div>

        <div class="integration-health-empty" v-if="!loading && !pendingChannels.length && statusList.length">
            <fa icon="check-circle" class="integration-health-empty-icon" />
            <p>Todas as integrações em dia</p>
            <small>Nenhum item aguardando processamento.</small>
        </div>

        <p class="integration-health-unavailable" v-if="!loading && !statusList.length">
            Status da fila indisponível no momento. Use "Atualizar status da fila" para tentar de novo.
        </p>

        <div class="integration-health-ok" v-if="pendingChannels.length && okChannels.length">
            <span class="integration-health-ok-label">Em dia</span>

            <router-link class="integration-health-chip"
                v-for="status in okChannels"
                :key="status.id"
                :to="queueLink(status.id)">
                <ChannelLogoCmp :channel="status.channel" :size="16" />
            </router-link>
        </div>
    </div>
</template>

<script>
import router from '@/router'
import ChannelLogoCmp from '@/channel/components/ChannelLogoCmp.vue'
import { ChannelStatusSynchronizationModel } from '@/monitoring/models/ChannelStatusSynchronizationModel'
import { createGraphic } from '@/utils/normalizers/GraphicNormalizer'
import { chartColors, chartToken } from '@/utils/constants/ChartTheme'
import { dangerColor } from '@/dashboard/constants/Color'

// Ordem de importância: o que trava venda primeiro.
const ENTITIES = [
    {key: 'order', label: 'Pedidos', color: dangerColor},
    {key: 'product_store', label: 'Estoque e preço', color: chartColors[1]},
    {key: 'hook_event', label: 'Eventos', color: chartColors[2]},
    {key: 'product', label: 'Produtos', color: chartColors[0]},
    {key: 'product_image', label: 'Imagens', color: chartColors[5]},
    {key: 'product_category', label: 'Categorias de produto', color: chartColors[3]},
    {key: 'attribute', label: 'Atributos', color: chartColors[4]},
    {key: 'brand', label: 'Marcas', color: chartColors[7]},
    {key: 'category', label: 'Categorias', color: chartColors[9]},
]

export default {
    name: 'ChannelQueueHealthCmp',
    components: {
        ChannelLogoCmp,
    },
    // Apresentacional: a tela de canais já busca os status da fila e passa aqui.
    props: {
        statusList: {
            type: Array,
            default: () => [],
        },
        channelList: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        activeStatusList() {
            return this.statusList
                .map((status) => {
                    const channel = this.channelList.find((c) => String(c.id) === String(status.id))

                    // Cópia: o status pertence à tabela da tela e não deve ganhar o canal por efeito colateral.
                    return channel
                        ? new ChannelStatusSynchronizationModel({...status, channel})
                        : status
                })
                .filter((status) => status.channel.isActive || status.isActive)
        },
        pendingChannels() {
            return this.activeStatusList
                .filter((s) => s.hasPendingSync())
                .sort((a, b) => this._pendingTotal(b) - this._pendingTotal(a))
        },
        okChannels() {
            return this.activeStatusList.filter((s) => !s.hasPendingSync())
        },
        chartOptions() {
            const pending = this.pendingChannels
            const goToQueue = (id) => router.push(this.queueLink(id))

            return createGraphic({
                chart: {
                    type: 'bar',
                    height: Math.max(140, 60 + pending.length * 44),
                },
                xAxis: {
                    categories: pending.map((s) => s.channel.name || s.name),
                    lineColor: 'transparent',
                    crosshair: false,
                    labels: {
                        style: {
                            color: chartToken('--chart-text'),
                            fontSize: '13px',
                            fontWeight: '600',
                        },
                    },
                },
                yAxis: {
                    allowDecimals: false,
                    labels: {
                        enabled: false,
                    },
                },
                legend: {
                    enabled: true,
                    reversed: false,
                },
                tooltip: {
                    shared: true,
                    formatter() {
                        const rows = this.points
                            .filter((p) => p.y > 0)
                            .map((p) => `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${p.y.toLocaleString('pt-BR')}</b>`)

                        return [`<b>${this.x}</b>`, ...rows, '<span style="color:var(--text-faint)">Clique para abrir a fila</span>'].join('<br>')
                    },
                },
                plotOptions: {
                    bar: {
                        stacking: 'normal',
                        borderRadius: 3,
                        borderWidth: 0,
                        pointWidth: 18,
                        cursor: 'pointer',
                        point: {
                            events: {
                                click() {
                                    const status = pending[this.index]

                                    if (status) {
                                        goToQueue(status.id)
                                    }
                                },
                            },
                        },
                    },
                },
                // Entidade sem pendência em nenhum canal não polui a legenda.
                series: ENTITIES
                    .filter((e) => pending.some((s) => Number(s[e.key]) > 0))
                    .map((e) => ({
                        type: 'bar',
                        name: e.label,
                        color: e.color,
                        data: pending.map((s) => Number(s[e.key]) || 0),
                    })),
            })
        },
    },
    methods: {
        queueLink(id) {
            return `/canais/${id}/configuracoes/informacoes/fila-processamento`
        },
        _pendingTotal(status) {
            return ENTITIES.reduce((acc, e) => acc + (Number(status[e.key]) || 0), 0)
        },
    },
}
</script>

<style lang="scss" scoped>
.integration-health-empty {
    padding: 28px 0 20px;
    text-align: center;

    p {
        margin: 8px 0 2px;
        font-size: 15px;
        font-weight: 600;
        color: var(--text-strong);
    }

    small {
        color: var(--text-faint);
    }
}

.integration-health-empty-icon {
    font-size: 30px;
    color: var(--success-solid);
}

.integration-health-unavailable {
    padding: 16px 0 4px;
    text-align: center;
    color: var(--text-faint);
}

.channel-queue-health-loading {
    margin-top: 4px;
    font-size: .8em;
    color: var(--text-faint);
}

.integration-health-ok {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 10px;
    padding-top: 12px;
    border-top: 1px solid var(--border-subtle);
}

.integration-health-ok-label {
    margin-right: 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
    color: var(--success-fg);
}

.integration-health-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px 3px 4px;
    font-size: 13px;
    color: var(--text);
    background-color: var(--surface-2);
    border: 1px solid var(--border-subtle);
    border-radius: 999px;
    transition: background-color .15s ease, border-color .15s ease;

    &:hover {
        text-decoration: none;
        background-color: var(--success-bg);
        border-color: var(--success-border);
    }
}
</style>
